/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";
import BaseButton from "components/common/Button/Base";
import BaseInput from "components/common/Input/Base";
import { PALETTE } from "styles/base/variables";
import { convertMobileNo } from "utils/converter";

interface PhoneEditDialogProps {
  isOpen: boolean;
  businessName: string;
  // 현재 화면에 보이는 번호 (kakaoMobileNo = 급여담당자 연락처 우선, 없으면 userMobileNo)
  currentNumber: string | null;
  // 급여담당자 연락처(Business.payrollManagerMobileNo)가 등록돼 있는지 (있어야 빈 값 저장 = 삭제 가능)
  hasKakaoMobileNo: boolean;
  onClose: () => void;
  // 숫자만 남긴 문자열을 넘긴다. 빈 문자열이면 급여담당자 연락처 삭제 의도.
  onSubmit: (digits: string) => void;
}

function PhoneEditDialog({
  isOpen,
  businessName,
  currentNumber,
  hasKakaoMobileNo,
  onClose,
  onSubmit
}: PhoneEditDialogProps) {
  const [value, setValue] = useState("");

  useEffect(() => {
    if (isOpen) {
      const digits = (currentNumber || "").replace(/[^0-9]/g, "");
      setValue(digits);
    }
  }, [isOpen, currentNumber]);

  const digits = value.replace(/[^0-9]/g, "");
  // 10~11자리 입력 = 등록/변경, 빈 값 = 급여담당자 연락처 삭제(등록된 게 있을 때만)
  const canSubmit =
    digits.length === 10 ||
    digits.length === 11 ||
    (digits.length === 0 && hasKakaoMobileNo);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        css={css`
          font-size: 16px;
          font-weight: 700;
        `}
      >
        알림톡 수신 번호 변경
      </DialogTitle>
      <DialogContent>
        <div
          css={css`
            font-size: 13px;
            color: ${PALETTE.textMid};
            margin-bottom: 10px;
          `}
        >
          {businessName}
          {currentNumber && (
            <span
              css={css`
                margin-left: 6px;
              `}
            >
              (현재 {convertMobileNo(currentNumber)})
            </span>
          )}
        </div>
        <BaseInput
          placeholder="휴대폰 번호 (숫자만)"
          value={value}
          onInput={(e) =>
            setValue(e.currentTarget.value.replace(/[^0-9]/g, ""))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) onSubmit(digits);
          }}
          css={css`
            width: 100%;
            box-sizing: border-box;
          `}
        />
        <div
          css={css`
            margin-top: 8px;
            font-size: 12px;
            color: ${PALETTE.textMid};
            line-height: 1.5;
          `}
        >
          알림톡을 받을 번호입니다. 사업자 정보의 <b>급여담당자 연락처</b>로
          저장되며, 숫자 10~11자리를 입력하세요.
          <br />
          {hasKakaoMobileNo &&
            "비운 채 저장하면 급여담당자 연락처가 삭제되고 조직 대표 번호가 표시됩니다."}
        </div>
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px;
        `}
      >
        <BaseButton
          variant="outlined"
          color={PALETTE.primary}
          onClick={onClose}
        >
          취소
        </BaseButton>
        <BaseButton
          variant="contained"
          color={PALETTE.primary}
          disabled={!canSubmit}
          onClick={() => onSubmit(digits)}
        >
          확인
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
}

export default PhoneEditDialog;
